import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Assignment,
  AssignmentSubmission,
  Attendance,
  ATTENDANCE_LOG_TYPE,
  ATTENDANCE_STATUS,
  ATTENDANCE_STATUS_CHECK_IN,
  AttendanceLog,
  BREAK_TIMER_STATUS,
  BreakTimer,
  Class,
  Enrollment,
  ENROLLMENT_STATUS,
  type IClass,
  Quiz,
  QuizAttempt,
  School,
  Semester,
  Student,
  Teacher,
} from "../../models";
import {
  recordAttendanceAudit,
  refreshAttendanceSummary,
} from "../../services/attendance-summary-service";
import { AuthenticatedRequest } from "../../types/auth-types";
import { APIResponse } from "../../types/response-types";
import { isSemesterLockedForClass } from "../../utils/attendance-semester-lock";
import {
  getClassSessionForDate,
  getClassSessionForDateUTC,
  getDayBounds,
  getUTCDayBounds,
} from "../../utils/class-session-utils";
import { logger } from "../../utils/logger";

function resolveStudentJoinDate(
  enrollmentDate: Date | undefined,
  classCreatedAt: Date | undefined,
): Date {
  if (enrollmentDate) {
    return new Date(enrollmentDate);
  }
  if (classCreatedAt) {
    return new Date(classCreatedAt);
  }
  return new Date();
}

/** UTC calendar day bounds for YYYY-MM-DD (matches teacher student attendance log grouping). */
function getUtcDayRangeFromDateString(dateResponse: string): {
  start: Date;
  end: Date;
} {
  const parts = dateResponse.split("-").map(Number);
  const y = parts[0] ?? 0;
  const mo = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  const start = new Date(Date.UTC(y, mo - 1, day, 0, 0, 0, 0));
  const end = new Date(Date.UTC(y, mo - 1, day + 1, 0, 0, 0, 0));
  return { start, end };
}

/** True if dateResponse (YYYY-MM-DD) is today's UTC calendar date. */
function isDateStringTodayUtc(dateResponse: string): boolean {
  const now = new Date();
  const todayStr = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")}`;
  return dateResponse === todayStr;
}

function parseStudentDayLogsForCheckInOut(
  logs: {
    logType: string;
    timestamp: Date;
    attendanceStatus?: string;
  }[],
): {
  checkIn: (typeof logs)[number] | null;
  checkOut: (typeof logs)[number] | null;
} {
  const sorted = [...logs].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );
  const checkIn = sorted.find(l => l.logType === ATTENDANCE_LOG_TYPE.CHECK_IN);
  if (!checkIn) {
    return { checkIn: null, checkOut: null };
  }
  const checkOut = sorted.find(
    l =>
      l.logType === ATTENDANCE_LOG_TYPE.CHECK_OUT &&
      l.timestamp > checkIn.timestamp,
  );
  return { checkIn, checkOut: checkOut ?? null };
}

/**
 * Check-in row for mobile list: `status` stays late/present from the log;
 * `statusLabel` is In Class / In Break while the session is in progress (UTC), else Present / Late Attendance.
 */
function buildAttendancePayloadFromCheckInLog(args: {
  checkIn: { _id: unknown; timestamp: Date; attendanceStatus?: string };
  checkOut: { timestamp: Date } | null;
  hasActiveBreak: boolean;
  dateResponse: string;
  classSchedule: Pick<IClass, "schedule">;
}): {
  id: string;
  status: string;
  statusLabel: string;
  remarks: string;
  markedAt: Date;
} {
  const { checkIn, checkOut, hasActiveBreak, dateResponse, classSchedule } =
    args;
  const logId = String(checkIn._id);

  const isLate = checkIn.attendanceStatus === ATTENDANCE_STATUS_CHECK_IN.LATE;
  const status = isLate ? ATTENDANCE_STATUS.LATE : ATTENDANCE_STATUS.PRESENT;

  const session = getClassSessionForDateUTC(classSchedule, dateResponse);
  const now = new Date();
  const isToday = isDateStringTodayUtc(dateResponse);

  const classInProgress =
    session !== null &&
    isToday &&
    now.getTime() >= session.sessionStart.getTime() &&
    now.getTime() <= session.sessionEnd.getTime() &&
    checkOut == null;

  if (classInProgress) {
    if (hasActiveBreak) {
      return {
        id: logId,
        status,
        statusLabel: "In Break",
        remarks: "",
        markedAt: checkIn.timestamp,
      };
    }
    return {
      id: logId,
      status,
      statusLabel: "In Class",
      remarks: "",
      markedAt: checkIn.timestamp,
    };
  }

  return {
    id: logId,
    status,
    statusLabel: isLate ? "Late Attendance" : "Present",
    remarks: "",
    markedAt: checkIn.timestamp,
  };
}

/**
 * Teacher Mobile Controller - Handles mobile-specific teacher operations
 * Separate from school flows to avoid conflicts
 */
export class TeacherMobileController {
  /**
   * Remove specific documents from teacher profile using media IDs (Mobile App)
   * This is separate from school administrative flows
   */
  async removeTeacherDocuments(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only teachers can remove their own documents via mobile
      if (userRole !== "teacher") {
        res.status(403).json({
          success: false,
          message:
            "Only teachers can remove their own documents via mobile app",
        } as APIResponse);
        return;
      }

      const { removeDocuments } = req.body;

      if (
        !removeDocuments ||
        !Array.isArray(removeDocuments) ||
        removeDocuments.length === 0
      ) {
        res.status(400).json({
          success: false,
          message:
            "Please provide removeDocuments array with media IDs to remove",
        } as APIResponse);
        return;
      }

      // Find teacher profile by userId
      const teacherProfile = await Teacher.findOne({ userId });
      if (!teacherProfile) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      if (!teacherProfile.documents || teacherProfile.documents.length === 0) {
        res.status(400).json({
          success: false,
          message: "No documents found to remove.",
        } as APIResponse);
        return;
      }

      // Remove documents by fileName (media ID)
      const originalCount = teacherProfile.documents.length;
      const documentsToRemove: string[] = [];

      teacherProfile.documents = teacherProfile.documents.filter((doc: any) => {
        if (removeDocuments.includes(doc.fileName)) {
          documentsToRemove.push(doc.fileName);
          return false;
        }
        return true;
      });

      const removedCount = originalCount - teacherProfile.documents.length;

      if (removedCount === 0) {
        res.status(404).json({
          success: false,
          message: "No matching documents found to remove.",
        } as APIResponse);
        return;
      }

      await teacherProfile.save();

      logger.info(
        `Teacher mobile: ${removedCount} documents removed from teacher profile for user: ${authReq.user.email}`,
        {
          userId: authReq.user.userId,
          employeeId: teacherProfile.employeeId,
          removedDocuments: documentsToRemove,
          source: "mobile",
        },
      );

      res.status(200).json({
        success: true,
        message: `${removedCount} document(s) removed successfully`,
        data: {
          removedCount,
          removedDocuments: documentsToRemove,
          remainingDocuments: teacherProfile.documents.length,
          documents: teacherProfile.documents,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Remove documents error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update teacher profile with document changes (Mobile App)
   */
  async updateTeacherProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only teachers can update their own profile via mobile
      if (userRole !== "teacher") {
        res.status(403).json({
          success: false,
          message: "Only teachers can update their own profile via mobile app",
        } as APIResponse);
        return;
      }

      const { removeDocuments } = req.body;

      if (!removeDocuments || !Array.isArray(removeDocuments)) {
        res.status(400).json({
          success: false,
          message: "Please provide removeDocuments array with media IDs",
        } as APIResponse);
        return;
      }

      // Find teacher profile by userId
      const teacherProfile = await Teacher.findOne({ userId });
      if (!teacherProfile) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      let removedCount = 0;
      const documentsToRemove: string[] = [];

      // Remove documents if requested
      if (removeDocuments.length > 0) {
        if (
          !teacherProfile.documents ||
          teacherProfile.documents.length === 0
        ) {
          res.status(400).json({
            success: false,
            message: "No documents found to remove.",
          } as APIResponse);
          return;
        }

        const originalCount = teacherProfile.documents.length;

        teacherProfile.documents = teacherProfile.documents.filter(
          (doc: any) => {
            if (removeDocuments.includes(doc.fileName)) {
              documentsToRemove.push(doc.fileName);
              return false;
            }
            return true;
          },
        );

        removedCount = originalCount - teacherProfile.documents.length;
      }

      await teacherProfile.save();

      logger.info(
        `Teacher mobile profile update: ${removedCount} documents removed for user: ${authReq.user.email}`,
        {
          userId: authReq.user.userId,
          employeeId: teacherProfile.employeeId,
          removedDocuments: documentsToRemove,
          source: "mobile",
        },
      );

      res.status(200).json({
        success: true,
        message: `Profile updated successfully. ${removedCount} document(s) removed.`,
        data: {
          removedCount,
          removedDocuments: documentsToRemove,
          remainingDocuments: teacherProfile.documents?.length || 0,
          documents: teacherProfile.documents || [],
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Update profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get teacher's classes (Mobile App)
   * @route GET /api/v1/mobile/teacher/classes
   * @access Private (Teacher)
   */
  async getClasses(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { semesterId, status } = req.query;

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      };

      if (semesterId) {
        filterQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      if (status) {
        filterQuery.status = status;
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const classes = await Class.find(filterQuery)
        .populate("semesterId", "semesterName academicYear")
        .sort({ createdAt: -1 });

      // Get student counts from Enrollment collection
      const classIds = classes.map(c => c._id);
      const enrollmentCounts = await mongoose.model("Enrollment").aggregate([
        {
          $match: {
            courseOfferingId: { $in: classIds },
            status: "enrolled",
          },
        },
        {
          $group: {
            _id: "$courseOfferingId",
            count: { $sum: 1 },
          },
        },
      ]);

      const enrollmentMap = new Map(
        enrollmentCounts.map(
          (e: { _id: mongoose.Types.ObjectId; count: number }) => [
            e._id.toString(),
            e.count,
          ],
        ),
      );

      res.status(200).json({
        success: true,
        message: "Classes retrieved successfully",
        data: {
          classes: classes.map(classItem => {
            const classId = classItem._id.toString();
            const semester = classItem.semesterId as unknown as
              | {
                  _id: mongoose.Types.ObjectId;
                  semesterName: string;
                  academicYear: string;
                }
              | undefined;

            return {
              id: classItem.id,
              className: classItem.className,
              classCode: classItem.classCode,
              courseCode: classItem.courseCode,
              courseName: classItem.courseName,
              subject: classItem.subject,
              status: classItem.status,
              isActive: classItem.isActive,
              currentStudents: enrollmentMap.get(classId) || 0,
              maxStudents: classItem.maxStudents,
              semester: semester,
              schedule: classItem.schedule,
              createdAt: classItem.createdAt,
            };
          }),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get classes error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get teacher's students (Mobile App)
   * @route GET /api/v1/mobile/teacher/students
   * @access Private (Teacher)
   */
  async getStudents(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.query;

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      };

      if (classId) {
        filterQuery._id = new mongoose.Types.ObjectId(classId as string);
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const classes = await Class.find(filterQuery).select("_id");

      // Get class IDs
      const classIds = classes.map(c => c._id);

      // Get enrollments for these classes
      const enrollments = await mongoose
        .model("Enrollment")
        .find({
          courseOfferingId: { $in: classIds },
          status: "enrolled",
        })
        .select("studentId");

      // Get unique student IDs from enrollments
      const studentIds = new Set<string>();
      for (const enrollment of enrollments) {
        studentIds.add(
          (enrollment.studentId as mongoose.Types.ObjectId).toString(),
        );
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const students = await Student.find({
        _id: {
          $in: [...studentIds].map(id => new mongoose.Types.ObjectId(id)),
        },
        schoolId: teacher.schoolId,
      }).select("fullName studentId profilePicture academicInfo");

      res.status(200).json({
        success: true,
        message: "Students retrieved successfully",
        data: {
          students: students.map(student => ({
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
            profilePicture: student.profilePicture,
            academicInfo: (student as any).academicInfo,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get students error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get teacher's semesters (Mobile App)
   * @route GET /api/v1/mobile/teacher/semesters
   * @access Private (Teacher)
   */
  async getSemesters(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Get teacher's classes to find unique semesters
      // eslint-disable-next-line unicorn/no-array-callback-reference
      const classes = await Class.find({
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      }).select("semesterId");

      const semesterIds = [
        ...new Set(classes.map(c => c.semesterId?.toString()).filter(Boolean)),
      ];

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const semesters = await Semester.find({
        _id: { $in: semesterIds.map(id => new mongoose.Types.ObjectId(id)) },
        schoolId: teacher.schoolId,
      }).sort({ startDate: -1 });

      res.status(200).json({
        success: true,
        message: "Semesters retrieved successfully",
        data: {
          semesters: semesters.map(semester => ({
            id: semester.id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            isCurrent: semester.isCurrent,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get semesters error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get teacher's attendance (Mobile App)
   * @route GET /api/v1/mobile/teacher/attendance
   * @access Private (Teacher)
   */
  async getAttendance(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, date, startDate, endDate } = req.query;

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        teacherId: teacher._id,
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (date) {
        const attendanceDate = new Date(date as string);
        attendanceDate.setHours(0, 0, 0, 0);
        const nextDay = new Date(attendanceDate);
        nextDay.setDate(nextDay.getDate() + 1);
        filterQuery.date = { $gte: attendanceDate, $lt: nextDay };
      } else if (startDate || endDate) {
        filterQuery.date = {};
        if (startDate) {
          filterQuery.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.date.$lte = new Date(endDate as string);
        }
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const attendance = await Attendance.find(filterQuery)
        .populate("class", "className courseCode")
        .populate("student", "fullName studentId profilePicture")
        .sort({ date: -1 });

      res.status(200).json({
        success: true,
        message: "Attendance retrieved successfully",
        data: {
          attendance: attendance.map(a => {
            const aDoc = a as any;
            return {
              id: a.id,
              class: aDoc.class
                ? {
                    id: aDoc.class.id || aDoc.class._id,
                    className: aDoc.class.className,
                    courseCode: aDoc.class.courseCode,
                  }
                : null,
              student: aDoc.student
                ? {
                    id: aDoc.student.id || aDoc.student._id,
                    fullName: aDoc.student.fullName,
                    studentId: aDoc.student.studentId,
                    profilePicture: aDoc.student.profilePicture,
                  }
                : null,
              status: a.status,
              remarks: a.remarks,
              date: a.date,
            };
          }),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get class students with attendance for a specific date (Mobile App).
   * For today's UTC date only: if a student has no manual attendance and no check-in log yet, `attendance` is null (not absent).
   * Check-in rows: `statusLabel` is In Class / In Break while the UTC session is in progress today; after session end (or past dates) Present / Late Attendance.
   * @route GET /api/v1/mobile/teacher/classes/:classId/attendance
   * @access Private - Teacher only
   * @query date (required) - Date in YYYY-MM-DD format
   */
  async getClassAttendance(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.params;
      const { date } = req.query;

      if (!date || typeof date !== "string") {
        res.status(400).json({
          success: false,
          message: "Date is required (format: YYYY-MM-DD)",
        } as APIResponse);
        return;
      }

      const trimmedDate = date.trim();
      const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedDate);
      if (!dateParts) {
        res.status(400).json({
          success: false,
          message: "Invalid date format (use YYYY-MM-DD)",
        } as APIResponse);
        return;
      }
      const dateResponse = `${dateParts[1]}-${dateParts[2]}-${dateParts[3]}`;

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Validate class ID
      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class (prefer school match, but do not block if schoolId missing/mismatched)
      const classQuery: any = {
        _id: classId,
        teacherIds: teacher._id,
      };
      if (teacher.schoolId) {
        classQuery.schoolId = teacher.schoolId;
      }

      let classItem = await Class.findOne(classQuery).populate(
        "semesterId",
        "semesterName academicYear",
      );

      // Fallback: if not found and schoolId filter blocked, retry without schoolId to avoid false negatives
      if (!classItem) {
        classItem = await Class.findOne({
          _id: classId,
          teacherIds: teacher._id,
        }).populate("semesterId", "semesterName academicYear");
      }

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      // Roster: Enrollment rows + class.studentIds (mark attendance validates studentIds; some rosters may lack Enrollment docs)
      const classObjectId = new mongoose.Types.ObjectId(classId);
      const enrollmentRows = await Enrollment.find({
        courseOfferingId: classObjectId,
        status: ENROLLMENT_STATUS.ENROLLED,
      })
        .select("studentId")
        .lean();

      const rosterStudentIds = new Set<string>();
      for (const row of enrollmentRows) {
        if (row.studentId) {
          rosterStudentIds.add(row.studentId.toString());
        }
      }
      for (const sid of classItem.studentIds ?? []) {
        rosterStudentIds.add(sid.toString());
      }

      // Parse date (same local-day bounds as markClassAttendance)
      const attendanceDate = new Date(dateResponse);
      attendanceDate.setHours(0, 0, 0, 0);
      if (Number.isNaN(attendanceDate.getTime())) {
        res.status(400).json({
          success: false,
          message: "Invalid date",
        } as APIResponse);
        return;
      }
      const nextDay = new Date(attendanceDate);
      nextDay.setDate(nextDay.getDate() + 1);

      // Get attendance records for this class and date
      const attendanceRecords = await Attendance.find({
        classId: classObjectId,
        date: {
          $gte: attendanceDate,
          $lt: nextDay,
        },
      });

      // Create a map of studentId -> attendance
      const attendanceMap = new Map<string, (typeof attendanceRecords)[0]>();
      for (const record of attendanceRecords) {
        attendanceMap.set(record.studentId.toString(), record);
      }

      const studentObjectIds = [...rosterStudentIds].map(
        id => new mongoose.Types.ObjectId(id),
      );

      const rosterStudents =
        studentObjectIds.length === 0
          ? []
          : await Student.find({
              _id: { $in: studentObjectIds },
            })
              .select("fullName studentId profilePicture userId")
              .lean();

      rosterStudents.sort((a, b) =>
        (a.fullName || "").localeCompare(b.fullName || "", undefined, {
          sensitivity: "base",
        }),
      );

      const studentUserIds = [
        ...new Set(
          rosterStudents
            .map(s => s.userId)
            .filter((uid): uid is mongoose.Types.ObjectId => uid != null),
        ),
      ];

      const { start: utcDayStart, end: utcDayEnd } =
        getUtcDayRangeFromDateString(dateResponse);

      const dayCheckInOutLogs =
        studentUserIds.length === 0
          ? []
          : await AttendanceLog.find({
              classId: classObjectId,
              userRole: "student",
              userId: { $in: studentUserIds },
              timestamp: { $gte: utcDayStart, $lt: utcDayEnd },
              logType: {
                $in: [
                  ATTENDANCE_LOG_TYPE.CHECK_IN,
                  ATTENDANCE_LOG_TYPE.CHECK_OUT,
                ],
              },
            })
              .sort({ timestamp: 1 })
              .lean();

      const logsByUserId = new Map<string, typeof dayCheckInOutLogs>();
      for (const log of dayCheckInOutLogs) {
        const uid = log.userId.toString();
        let bucket = logsByUserId.get(uid);
        if (!bucket) {
          bucket = [];
          logsByUserId.set(uid, bucket);
        }
        bucket.push(log);
      }

      const activeBreakDocs =
        studentUserIds.length === 0
          ? []
          : await BreakTimer.find({
              userId: { $in: studentUserIds },
              classId: classObjectId,
              status: BREAK_TIMER_STATUS.ACTIVE,
            })
              .select("userId")
              .lean();

      const activeBreakUserIds = new Set(
        activeBreakDocs.map(b => b.userId.toString()),
      );

      const classSchedule = classItem as Pick<IClass, "schedule">;

      const studentsWithAttendance = rosterStudents.map(student => {
        const studentIdStr = new mongoose.Types.ObjectId(
          student._id,
        ).toString();
        const attendance = attendanceMap.get(studentIdStr);
        let status = "Not Marked";
        let statusLabel = "Not Marked";
        let remarks = "";

        if (attendance) {
          status = attendance.status;
          remarks = attendance.remarks || "";

          // Map status to display labels
          switch (attendance.status) {
            case ATTENDANCE_STATUS.PRESENT: {
              statusLabel = "Present";
              break;
            }
            case ATTENDANCE_STATUS.LATE: {
              statusLabel = "Late Attendance";
              break;
            }
            case ATTENDANCE_STATUS.ABSENT: {
              statusLabel = "Absent";
              break;
            }
            case ATTENDANCE_STATUS.EXCUSED: {
              statusLabel = remarks.toLowerCase().includes("restroom")
                ? "Restroom Break"
                : "Excused";
              break;
            }
            case ATTENDANCE_STATUS.HALF_DAY: {
              statusLabel = remarks.toLowerCase().includes("restroom")
                ? "Restroom Break"
                : "Half Day";
              break;
            }
            default: {
              statusLabel = attendance.status;
            }
          }
        }

        const userIdStr = student.userId?.toString();
        const userLogs = userIdStr ? (logsByUserId.get(userIdStr) ?? []) : [];
        const { checkIn, checkOut } =
          parseStudentDayLogsForCheckInOut(userLogs);

        let attendancePayload: {
          id?: string;
          status: string;
          statusLabel: string;
          remarks: string;
          markedAt: Date | null;
        } | null;

        if (attendance) {
          attendancePayload = {
            id: attendance.id,
            status,
            statusLabel,
            remarks,
            markedAt: attendance.markedAt,
          };
        } else if (checkIn) {
          const hasActiveBreak = userIdStr
            ? activeBreakUserIds.has(userIdStr)
            : false;
          attendancePayload = buildAttendancePayloadFromCheckInLog({
            checkIn: {
              _id: (checkIn as { _id?: unknown })._id,
              timestamp: checkIn.timestamp,
              attendanceStatus: checkIn.attendanceStatus,
            },
            checkOut: checkOut ? { timestamp: checkOut.timestamp } : null,
            hasActiveBreak,
            dateResponse,
            classSchedule,
          });
        } else if (isDateStringTodayUtc(dateResponse)) {
          attendancePayload = null;
        } else {
          attendancePayload = {
            status: ATTENDANCE_STATUS.ABSENT,
            statusLabel: "",
            remarks: "",
            markedAt: null,
          };
        }

        return {
          id: studentIdStr,
          fullName: student.fullName,
          studentId: student.studentId,
          profilePicture: student.profilePicture,
          attendance: attendancePayload,
        };
      });

      res.status(200).json({
        success: true,
        message: "Class attendance retrieved successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            classCreatedAt: classItem.createdAt,
          },
          date: dateResponse,
          semester: (classItem as any).semesterId
            ? {
                id: (classItem as any).semesterId._id,
                semesterName: (classItem as any).semesterId.semesterName,
                academicYear: (classItem as any).semesterId.academicYear,
              }
            : null,
          students: studentsWithAttendance,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get class attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Mark attendance for a class (Mobile App)
   * @route POST /api/v1/teacher/classes/:classId/attendance
   * @access Private - Teacher only
   * @body (format A) { studentId: string, attendance: string[] } - Mark one student present for multiple dates (check-in at class start, check-out at class end)
   * @body (format B) { date: string, attendance: [{ studentId, status, remarks? }] } - Legacy: one date, multiple students
   */
  async markClassAttendance(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.params;
      const { date, attendance, studentId: bodyStudentId } = req.body;

      // New format: single student, multiple dates (mark present with auto check-in/check-out)
      const isMultiDatePayload =
        bodyStudentId &&
        typeof bodyStudentId === "string" &&
        Array.isArray(attendance) &&
        attendance.length > 0 &&
        attendance.every((d: unknown) => typeof d === "string");

      if (isMultiDatePayload) {
        await this.markStudentPresentForMultipleDates(
          req,
          res,
          userId,
          classId,
          bodyStudentId,
          attendance,
        );
        return;
      }

      // Legacy format: one date, multiple students
      if (!date || !attendance || !Array.isArray(attendance)) {
        res.status(400).json({
          success: false,
          message: "Date and attendance array are required",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Validate class ID
      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const classItem = await Class.findOne({
        _id: classId,
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      }).populate("semesterId", "_id");

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      if (await isSemesterLockedForClass(classId)) {
        res.status(409).json({
          success: false,
          message: "Attendance changes are locked for this semester",
        } as APIResponse);
        return;
      }

      // Parse date
      const attendanceDate = new Date(date);
      attendanceDate.setHours(0, 0, 0, 0);

      if (Number.isNaN(attendanceDate.getTime())) {
        res.status(400).json({
          success: false,
          message: "Invalid date format",
        } as APIResponse);
        return;
      }

      const markedAttendance = [];
      const errors = [];

      for (const record of attendance) {
        const { studentId, status, remarks } = record;

        if (!studentId || !status) {
          errors.push({
            studentId,
            error: "Student ID and status are required",
          });
          continue;
        }

        // Validate student belongs to class
        if (
          !classItem.studentIds?.some(
            id => id.toString() === studentId.toString(),
          )
        ) {
          errors.push({
            studentId,
            error: "Student is not enrolled in this class",
          });
          continue;
        }

        // Validate status
        if (!Object.values(ATTENDANCE_STATUS).includes(status)) {
          errors.push({
            studentId,
            error: `Invalid status. Must be one of: ${Object.values(ATTENDANCE_STATUS).join(", ")}`,
          });
          continue;
        }

        // Check if attendance already marked
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const existing = await Attendance.findOne({
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          classId: new mongoose.Types.ObjectId(classId),
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          studentId: new mongoose.Types.ObjectId(studentId),
          date: {
            $gte: attendanceDate,
            $lt: new Date(attendanceDate.getTime() + 24 * 60 * 60 * 1000),
          },
        });

        if (existing) {
          // Update existing attendance
          existing.status = status;
          existing.remarks = remarks || "";
          existing.markedBy = new mongoose.Types.ObjectId(userId);
          existing.markedAt = new Date();
          existing.teacherId = teacher._id;
          await existing.save();
          markedAttendance.push(existing);
        } else {
          // Create new attendance
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          const newAttendance = new Attendance({
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            schoolId: new mongoose.Types.ObjectId(teacher.schoolId.toString()),
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            classId: new mongoose.Types.ObjectId(classId),
            semesterId: classItem.semesterId,
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            studentId: new mongoose.Types.ObjectId(studentId),
            teacherId: teacher._id,
            date: attendanceDate,
            status,
            remarks: remarks || "",
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            markedBy: new mongoose.Types.ObjectId(userId),
            markedAt: new Date(),
          });
          await newAttendance.save();
          markedAttendance.push(newAttendance);
        }
      }

      logger.info(`Teacher marked attendance for class: ${classId}`, {
        classId,
        teacherId: teacher.id,
        date: attendanceDate,
        markedCount: markedAttendance.length,
      });

      res.status(200).json({
        success: true,
        message: `Attendance marked successfully for ${markedAttendance.length} student(s)`,
        data: {
          date: attendanceDate.toISOString().split("T")[0],
          markedCount: markedAttendance.length,
          errors: errors.length > 0 ? errors : undefined,
          attendance: markedAttendance.map(a => ({
            id: a.id,
            studentId: a.studentId,
            status: a.status,
            remarks: a.remarks,
            markedAt: a.markedAt,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Mark class attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Mark one student present for multiple dates: creates check-in at class start and check-out at class end for each date.
   * Used when body is { studentId, attendance: [ "YYYY-MM-DD", ... ] }.
   */
  private async markStudentPresentForMultipleDates(
    req: Request,
    res: Response,
    userId: string,
    classId: string,
    studentId: string,
    dateStrings: string[],
  ): Promise<void> {
    if (!mongoose.Types.ObjectId.isValid(classId)) {
      res.status(400).json({
        success: false,
        message: "Invalid class ID",
      } as APIResponse);
      return;
    }
    if (!mongoose.Types.ObjectId.isValid(studentId)) {
      res.status(400).json({
        success: false,
        message: "Invalid student ID",
      } as APIResponse);
      return;
    }

    const teacher = await Teacher.findOne({
      userId: new mongoose.Types.ObjectId(userId),
    });
    if (!teacher) {
      res.status(404).json({
        success: false,
        message: "Teacher profile not found",
      } as APIResponse);
      return;
    }

    const classItem = await Class.findOne({
      _id: classId,
      teacherIds: teacher._id,
      schoolId: teacher.schoolId,
    }).lean();
    if (!classItem) {
      res.status(404).json({
        success: false,
        message: "Class not found or you are not assigned to this class",
      } as APIResponse);
      return;
    }

    const student = await Student.findById(studentId).select("userId").lean();
    if (!student?.userId) {
      res.status(404).json({
        success: false,
        message: "Student not found",
      } as APIResponse);
      return;
    }

    const studentObjectId = new mongoose.Types.ObjectId(studentId);
    const isEnrolled = classItem.studentIds?.some(
      (id: mongoose.Types.ObjectId) =>
        id.toString() === studentObjectId.toString(),
    );
    if (!isEnrolled) {
      res.status(403).json({
        success: false,
        message: "Student is not enrolled in this class",
      } as APIResponse);
      return;
    }

    const classObjectId = new mongoose.Types.ObjectId(classId);
    const userObjectId = student.userId;
    const schoolId = classItem.schoolId.toString();
    const semesterId = classItem.semesterId as
      | mongoose.Types.ObjectId
      | undefined;

    const markedDates: string[] = [];
    const errors: { date: string; error: string }[] = [];

    for (const dateStr of dateStrings) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        errors.push({
          date: dateStr,
          error: "Invalid date format (use YYYY-MM-DD)",
        });
        continue;
      }

      const session = getClassSessionForDateUTC(classItem, dateStr);
      if (!session) {
        errors.push({
          date: dateStr,
          error: "Class has no schedule for this day",
        });
        continue;
      }

      const { start: dayStart, end: dayEnd } = getUTCDayBounds(dateStr);
      const attendanceDateUTC = new Date(dayStart);

      const existingAttendance = await Attendance.findOne({
        classId: classObjectId,
        studentId: studentObjectId,
        date: {
          $gte: dayStart,
          $lt: new Date(dayEnd.getTime() + 1),
        },
      });

      if (existingAttendance) {
        existingAttendance.status = ATTENDANCE_STATUS.PRESENT;
        existingAttendance.markedBy = new mongoose.Types.ObjectId(userId);
        existingAttendance.markedAt = new Date();
        existingAttendance.teacherId = teacher._id;
        await existingAttendance.save();
      } else {
        await new Attendance({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          classId: classObjectId,
          semesterId,
          studentId: studentObjectId,
          teacherId: teacher._id,
          date: attendanceDateUTC,
          status: ATTENDANCE_STATUS.PRESENT,
          remarks: "",
          markedBy: new mongoose.Types.ObjectId(userId),
          markedAt: new Date(),
        }).save();
      }

      markedDates.push(dateStr);
    }

    logger.info("Teacher marked student present for multiple dates", {
      classId,
      studentId,
      teacherId: teacher.id,
      markedCount: markedDates.length,
      dates: markedDates,
    });

    res.status(200).json({
      success: true,
      message: `Marked present for ${markedDates.length} date(s)`,
      data: {
        studentId,
        classId,
        markedDates,
        errors: errors.length > 0 ? errors : undefined,
      },
    } as APIResponse);
  }

  /**
   * Update individual student attendance (Mobile App)
   * @route PUT /api/v1/mobile/teacher/classes/:classId/attendance/:studentId
   * @access Private - Teacher only
   * @body { status: string, remarks?: string }
   */
  async updateStudentAttendance(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, studentId } = req.params;
      const { status, remarks, date } = req.body;

      if (!status) {
        res.status(400).json({
          success: false,
          message: "Status is required",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Validate IDs
      if (
        !mongoose.Types.ObjectId.isValid(classId) ||
        !mongoose.Types.ObjectId.isValid(studentId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID or student ID",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const classItem = await Class.findOne({
        _id: classId,
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      if (await isSemesterLockedForClass(classId)) {
        res.status(409).json({
          success: false,
          message: "Attendance changes are locked for this semester",
        } as APIResponse);
        return;
      }

      // Validate status
      if (!Object.values(ATTENDANCE_STATUS).includes(status)) {
        res.status(400).json({
          success: false,
          message: `Invalid status. Must be one of: ${Object.values(ATTENDANCE_STATUS).join(", ")}`,
        } as APIResponse);
        return;
      }

      // Parse date (use provided date or today)
      const attendanceDate = date ? new Date(date) : new Date();
      attendanceDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(attendanceDate);
      nextDay.setDate(nextDay.getDate() + 1);

      const studentObjectId = new mongoose.Types.ObjectId(studentId);
      const student = await Student.findById(studentObjectId)
        .select("userId")
        .lean();

      if (student?.userId) {
        const hasCheckIn = await AttendanceLog.exists({
          userId: student.userId,
          classId: new mongoose.Types.ObjectId(classId),
          userRole: "student",
          logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
          timestamp: {
            $gte: attendanceDate,
            $lt: nextDay,
          },
        });

        if (hasCheckIn) {
          res.status(403).json({
            success: false,
            message:
              "Cannot override attendance after the student has checked in",
          } as APIResponse);
          return;
        }
      }

      // Find existing attendance
      const existing = await Attendance.findOne({
        classId: new mongoose.Types.ObjectId(classId),
        studentId: new mongoose.Types.ObjectId(studentId),
        date: {
          $gte: attendanceDate,
          $lt: nextDay,
        },
      });

      if (existing) {
        const previousStatus = existing.status;
        // Update existing
        existing.status = status;
        existing.remarks = remarks || "";
        existing.markedBy = new mongoose.Types.ObjectId(userId);
        existing.markedAt = new Date();
        existing.teacherId = teacher._id;
        await existing.save();

        if (previousStatus !== status) {
          await recordAttendanceAudit({
            schoolId: teacher.schoolId,
            classId: new mongoose.Types.ObjectId(classId),
            studentId: studentObjectId,
            date: attendanceDate,
            changedBy: new mongoose.Types.ObjectId(userId),
            changedByRole: "teacher",
            fromStatus: previousStatus,
            toStatus: status,
            reason: remarks,
          });
        }

        const enrollment = await Enrollment.findOne({
          studentId: studentObjectId,
          courseOfferingId: new mongoose.Types.ObjectId(classId),
        })
          .select("enrollmentDate")
          .lean();
        const joinDate = resolveStudentJoinDate(
          enrollment?.enrollmentDate
            ? new Date(enrollment.enrollmentDate)
            : undefined,
          classItem.createdAt ? new Date(classItem.createdAt) : undefined,
        );
        await refreshAttendanceSummary(
          new mongoose.Types.ObjectId(classId),
          studentObjectId,
          joinDate,
        );

        res.status(200).json({
          success: true,
          message: "Attendance updated successfully",
          data: {
            attendance: {
              id: existing.id,
              studentId: existing.studentId,
              status: existing.status,
              remarks: existing.remarks,
              markedAt: existing.markedAt,
            },
          },
        } as APIResponse);
      } else {
        // Create new
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const newAttendance = new Attendance({
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          schoolId: new mongoose.Types.ObjectId(teacher.schoolId.toString()),
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          classId: new mongoose.Types.ObjectId(classId),
          semesterId: classItem.semesterId,
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          studentId: new mongoose.Types.ObjectId(studentId),
          teacherId: teacher._id,
          date: attendanceDate,
          status,
          remarks: remarks || "",
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          markedBy: new mongoose.Types.ObjectId(userId),
          markedAt: new Date(),
        });
        await newAttendance.save();

        await recordAttendanceAudit({
          schoolId: teacher.schoolId,
          classId: new mongoose.Types.ObjectId(classId),
          studentId: studentObjectId,
          date: attendanceDate,
          changedBy: new mongoose.Types.ObjectId(userId),
          changedByRole: "teacher",
          toStatus: status,
          reason: remarks,
        });

        const enrollment = await Enrollment.findOne({
          studentId: studentObjectId,
          courseOfferingId: new mongoose.Types.ObjectId(classId),
        })
          .select("enrollmentDate")
          .lean();
        const joinDate = resolveStudentJoinDate(
          enrollment?.enrollmentDate
            ? new Date(enrollment.enrollmentDate)
            : undefined,
          classItem.createdAt ? new Date(classItem.createdAt) : undefined,
        );
        await refreshAttendanceSummary(
          new mongoose.Types.ObjectId(classId),
          studentObjectId,
          joinDate,
        );

        res.status(201).json({
          success: true,
          message: "Attendance marked successfully",
          data: {
            attendance: {
              id: newAttendance.id,
              studentId: newAttendance.studentId,
              status: newAttendance.status,
              remarks: newAttendance.remarks,
              markedAt: newAttendance.markedAt,
            },
          },
        } as APIResponse);
      }
    } catch (error) {
      logger.error("Teacher mobile - Update student attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get class by ID with detailed information (Mobile App)
   * @route GET /api/v1/mobile/teacher/classes/:classId
   * @access Private - Teacher only
   */
  async getClassById(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.params;

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const classItem = await Class.findOne({
        _id: new mongoose.Types.ObjectId(classId),
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      })
        .populate("semesterId", "semesterName academicYear startDate endDate")
        .populate("teacherIds", "fullName employeeId profilePicture")
        .populate(
          "studentIds",
          "fullName studentId profilePicture academicLevel",
        )
        .populate("createdBy", "fullName email");

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      // Get enrolled students count
      const enrolledStudentsCount = classItem.studentIds?.length || 0;

      res.status(200).json({
        success: true,
        message: "Class details retrieved successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            subject: classItem.subject,
            credits: classItem.credits,
            description: classItem.description,
            status: classItem.status,
            isActive: classItem.isActive,
            currentStudents: enrolledStudentsCount,
            maxStudents: classItem.maxStudents,
            classRoom: classItem.classRoom,
            classRoomNumber: classItem.classRoomNumber,
            restRoomMinutes: classItem.restRoomMinutes,
            schedule: classItem.schedule || [],
            materials: classItem.materials || [],
            semester: (classItem.semesterId as any)
              ? {
                  id: (classItem.semesterId as any).id,
                  semesterName: (classItem.semesterId as any).semesterName,
                  academicYear: (classItem.semesterId as any).academicYear,
                  startDate: (classItem.semesterId as any).startDate,
                  endDate: (classItem.semesterId as any).endDate,
                }
              : null,
            teachers:
              (classItem.teacherIds as any[])?.map(t => ({
                id: t.id || t._id,
                fullName: t.fullName,
                employeeId: t.employeeId,
                profilePicture: t.profilePicture,
              })) || [],
            students:
              (classItem.studentIds as any[])?.map(s => ({
                id: s.id || s._id,
                fullName: s.fullName,
                studentId: s.studentId,
                profilePicture: s.profilePicture,
                academicLevel: s.academicLevel,
              })) || [],
            createdBy: (classItem.createdBy as any)
              ? {
                  id: (classItem.createdBy as any).id,
                  fullName: (classItem.createdBy as any).fullName,
                  email: (classItem.createdBy as any).email,
                }
              : null,
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get class by ID error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get all schools for dropdown (Mobile App - Teacher Signup)
   * @route GET /api/v1/mobile/teacher/schools
   * @access Public (for teacher signup)
   */
  async getAllSchools(req: Request, res: Response): Promise<void> {
    try {
      const schools = await School.find(
        {
          status: "approved",
          isVerified: true,
        },
        {
          _id: 1,
          schoolName: 1,
          schoolCode: 1,
          address: 1,
        },
      )
        .sort({ schoolName: 1 })
        .limit(1000);

      res.status(200).json({
        success: true,
        message: "Schools retrieved successfully",
        data: {
          schools: schools.map(school => ({
            id: school.id,
            schoolName: school.schoolName,
            schoolCode: school.schoolCode,
            address: school.address,
          })),
          totalCount: schools.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get all schools error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get quizzes for a class (Mobile App)
   * @route GET /api/v1/mobile/teacher/classes/:classId/quizzes
   * @access Private - Teacher only
   */
  async getClassQuizzes(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.params;
      const { status, page = 1, limit = 50 } = req.query;

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const classItem = await Class.findOne({
        _id: new mongoose.Types.ObjectId(classId),
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        classId: new mongoose.Types.ObjectId(classId),
        schoolId: teacher.schoolId,
      };

      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const quizzes = await Quiz.find(filterQuery)
        .populate("createdBy", "fullName email")
        .sort({ deadline: 1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Quiz.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Quizzes retrieved successfully",
        data: {
          quizzes: quizzes.map(quiz => ({
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            totalMarks: quiz.totalMarks,
            status: quiz.status,
            createdAt: quiz.createdAt,
            createdBy: (quiz.createdBy as any)
              ? {
                  id: (quiz.createdBy as any).id,
                  fullName: (quiz.createdBy as any).fullName,
                  email: (quiz.createdBy as any).email,
                }
              : null,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get class quizzes error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get assignments for a class (Mobile App)
   * @route GET /api/v1/mobile/teacher/classes/:classId/assignments
   * @access Private - Teacher only
   */
  async getClassAssignments(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId } = req.params;
      const { status, page = 1, limit = 50 } = req.query;

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const classItem = await Class.findOne({
        _id: new mongoose.Types.ObjectId(classId),
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or you are not assigned to this class",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        classId: new mongoose.Types.ObjectId(classId),
        schoolId: teacher.schoolId,
      };

      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const assignments = await Assignment.find(filterQuery)
        .populate("createdBy", "fullName email")
        .sort({ deadline: 1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Assignment.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Assignments retrieved successfully",
        data: {
          assignments: assignments.map(assignment => ({
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            allowLateSubmission: assignment.allowLateSubmission,
            lateSubmissionPenalty: assignment.lateSubmissionPenalty,
            status: assignment.status,
            createdAt: assignment.createdAt,
            createdBy: (assignment.createdBy as any)
              ? {
                  id: (assignment.createdBy as any).id,
                  fullName: (assignment.createdBy as any).fullName,
                  email: (assignment.createdBy as any).email,
                }
              : null,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get class assignments error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get quiz submissions with filters (Mobile App)
   * @route GET /api/v1/mobile/teacher/quizzes/:quizId/submissions
   * @access Private - Teacher only
   */
  async getQuizSubmissions(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { quizId } = req.params;
      const { filter = "all", page = 1, limit = 50 } = req.query;

      if (!mongoose.Types.ObjectId.isValid(quizId)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Verify quiz belongs to teacher's school
      const quiz = await Quiz.findOne({
        _id: new mongoose.Types.ObjectId(quizId),
        schoolId: teacher.schoolId,
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found or you don't have access to this quiz",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        quizId: new mongoose.Types.ObjectId(quizId),
      };

      // Apply filters
      switch (filter) {
        case "submitted": {
          filterQuery.status = { $in: ["submitted", "graded"] };

          break;
        }
        case "late_submitted":
        case "re_submitted": {
          filterQuery.status = { $in: ["submitted", "graded"] };
          filterQuery.isLateSubmission = true;

          break;
        }
        case "not_submitted": {
          // Get all students in class and find who hasn't submitted
          const classItem = await Class.findById(quiz.classId);
          if (classItem && classItem.studentIds) {
            const submittedStudentIds = await QuizAttempt.distinct(
              "studentId",
              {
                quizId: new mongoose.Types.ObjectId(quizId),
                status: { $in: ["submitted", "graded"] },
              },
            );

            const notSubmittedIds = classItem.studentIds.filter(
              id =>
                !submittedStudentIds.some(
                  sid => sid.toString() === id.toString(),
                ),
            );

            if (notSubmittedIds.length === 0) {
              res.status(200).json({
                success: true,
                message: "All students have submitted",
                data: {
                  submissions: [],
                  pagination: {
                    page: 1,
                    limit: 50,
                    total: 0,
                    pages: 0,
                  },
                },
              } as APIResponse);
              return;
            }

            // Return students who haven't submitted
            const students = await Student.find({
              _id: { $in: notSubmittedIds },
            }).select("fullName studentId profilePicture");

            res.status(200).json({
              success: true,
              message: "Not submitted students retrieved successfully",
              data: {
                submissions: students.map(student => ({
                  id: student.id,
                  student: {
                    id: student.id,
                    fullName: student.fullName,
                    studentId: student.studentId,
                    profilePicture: student.profilePicture,
                  },
                  status: "not_submitted",
                  submittedAt: null,
                  isLateSubmission: false,
                })),
                pagination: {
                  page: 1,
                  limit: students.length,
                  total: students.length,
                  pages: 1,
                },
              },
            } as APIResponse);
            return;
          }

          break;
        }
        // No default
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const submissions = await QuizAttempt.find(filterQuery)
        .populate("studentId", "fullName studentId profilePicture")
        .sort({ submittedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await QuizAttempt.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Quiz submissions retrieved successfully",
        data: {
          submissions: submissions.map(submission => ({
            id: submission.id,
            student: (submission.studentId as any)
              ? {
                  id: (submission.studentId as any).id,
                  fullName: (submission.studentId as any).fullName,
                  studentId: (submission.studentId as any).studentId,
                  profilePicture: (submission.studentId as any).profilePicture,
                }
              : null,
            status: submission.status,
            totalMarksObtained: submission.totalMarksObtained,
            submittedAt: submission.submittedAt,
            uploadedAt: submission.submittedAt, // For frontend compatibility
            isLateSubmission:
              submission.submittedAt && quiz.deadline
                ? new Date(submission.submittedAt) > new Date(quiz.deadline)
                : false,
            attemptNumber: submission.attemptNumber,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get quiz submissions error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get assignment submissions with filters (Mobile App)
   * @route GET /api/v1/mobile/teacher/assignments/:assignmentId/submissions
   * @access Private - Teacher only
   */
  async getAssignmentSubmissions(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { assignmentId } = req.params;
      const { filter = "all", page = 1, limit = 50 } = req.query;

      if (!mongoose.Types.ObjectId.isValid(assignmentId)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // Verify assignment belongs to teacher's school
      const assignment = await Assignment.findOne({
        _id: new mongoose.Types.ObjectId(assignmentId),
        schoolId: teacher.schoolId,
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message:
            "Assignment not found or you don't have access to this assignment",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        assignmentId: new mongoose.Types.ObjectId(assignmentId),
      };

      // Apply filters
      switch (filter) {
        case "submitted": {
          filterQuery.status = { $in: ["submitted", "graded"] };

          break;
        }
        case "late_submitted":
        case "re_submitted": {
          filterQuery.status = { $in: ["submitted", "graded"] };
          filterQuery.isLateSubmission = true;

          break;
        }
        case "not_submitted": {
          // Get all students in class and find who hasn't submitted
          const classItem = await Class.findById(assignment.classId);
          if (classItem && classItem.studentIds) {
            const submittedStudentIds = await AssignmentSubmission.distinct(
              "studentId",
              {
                assignmentId: new mongoose.Types.ObjectId(assignmentId),
                status: { $in: ["submitted", "graded"] },
              },
            );

            const notSubmittedIds = classItem.studentIds.filter(
              id =>
                !submittedStudentIds.some(
                  sid => sid.toString() === id.toString(),
                ),
            );

            if (notSubmittedIds.length === 0) {
              res.status(200).json({
                success: true,
                message: "All students have submitted",
                data: {
                  submissions: [],
                  pagination: {
                    page: 1,
                    limit: 50,
                    total: 0,
                    pages: 0,
                  },
                },
              } as APIResponse);
              return;
            }

            // Return students who haven't submitted
            const students = await Student.find({
              _id: { $in: notSubmittedIds },
            }).select("fullName studentId profilePicture");

            res.status(200).json({
              success: true,
              message: "Not submitted students retrieved successfully",
              data: {
                submissions: students.map(student => ({
                  id: student.id,
                  student: {
                    id: student.id,
                    fullName: student.fullName,
                    studentId: student.studentId,
                    profilePicture: student.profilePicture,
                  },
                  status: "not_submitted",
                  submittedAt: null,
                  isLateSubmission: false,
                })),
                pagination: {
                  page: 1,
                  limit: students.length,
                  total: students.length,
                  pages: 1,
                },
              },
            } as APIResponse);
          }

          break;
        }
        // No default
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const submissions = await AssignmentSubmission.find(filterQuery)
        .populate("studentId", "fullName studentId profilePicture")
        .sort({ submittedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await AssignmentSubmission.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Assignment submissions retrieved successfully",
        data: {
          submissions: submissions.map(submission => ({
            id: submission.id,
            student: (submission.studentId as any)
              ? {
                  id: (submission.studentId as any).id,
                  fullName: (submission.studentId as any).fullName,
                  studentId: (submission.studentId as any).studentId,
                  profilePicture: (submission.studentId as any).profilePicture,
                }
              : null,
            status: submission.status,
            marksObtained: submission.marksObtained,
            submittedAt: submission.submittedAt,
            uploadedAt: submission.submittedAt, // For frontend compatibility
            isLateSubmission: submission.isLateSubmission,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get assignment submissions error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get teacher reports/statistics (Mobile App)
   * @route GET /api/v1/mobile/teacher/reports
   * @access Private - Teacher only
   */
  async getReports(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, semesterId, startDate, endDate } = req.query;

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      };

      if (classId) {
        filterQuery._id = new mongoose.Types.ObjectId(classId as string);
      }

      if (semesterId) {
        filterQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      // Get classes
      // eslint-disable-next-line unicorn/no-array-callback-reference
      const classes = await Class.find(filterQuery).select("_id className");

      const classIds = classes.map(c => c._id);

      // Get attendance statistics
      const attendanceFilter: any = {
        classId: { $in: classIds },
        teacherId: teacher._id,
      };

      if (startDate || endDate) {
        attendanceFilter.date = {};
        if (startDate) {
          attendanceFilter.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          attendanceFilter.date.$lte = new Date(endDate as string);
        }
      }

      const totalAttendanceRecords =
        await Attendance.countDocuments(attendanceFilter);
      const presentCount = await Attendance.countDocuments({
        ...attendanceFilter,
        status: ATTENDANCE_STATUS.PRESENT,
      });
      const absentCount = await Attendance.countDocuments({
        ...attendanceFilter,
        status: ATTENDANCE_STATUS.ABSENT,
      });
      const lateCount = await Attendance.countDocuments({
        ...attendanceFilter,
        status: ATTENDANCE_STATUS.LATE,
      });

      // Get assignment statistics
      const assignmentFilter: any = {
        classId: { $in: classIds },
        schoolId: teacher.schoolId,
      };

      const totalAssignments =
        await Assignment.countDocuments(assignmentFilter);
      const publishedAssignments = await Assignment.countDocuments({
        ...assignmentFilter,
        status: "published",
      });

      // Get quiz statistics
      const quizFilter: any = {
        classId: { $in: classIds },
        schoolId: teacher.schoolId,
      };

      const totalQuizzes = await Quiz.countDocuments(quizFilter);
      const publishedQuizzes = await Quiz.countDocuments({
        ...quizFilter,
        status: "published",
      });

      // Get total students across all classes
      const uniqueStudentIds = new Set<string>();
      for (const classItem of classes) {
        const classDetails = await Class.findById(classItem._id).select(
          "studentIds",
        );
        if (classDetails?.studentIds) {
          for (const studentId of classDetails.studentIds) {
            uniqueStudentIds.add(studentId.toString());
          }
        }
      }

      res.status(200).json({
        success: true,
        message: "Reports retrieved successfully",
        data: {
          summary: {
            totalClasses: classes.length,
            totalStudents: uniqueStudentIds.size,
            totalAssignments,
            publishedAssignments,
            totalQuizzes,
            publishedQuizzes,
          },
          attendance: {
            totalRecords: totalAttendanceRecords,
            present: presentCount,
            absent: absentCount,
            late: lateCount,
            attendanceRate:
              totalAttendanceRecords > 0
                ? ((presentCount / totalAttendanceRecords) * 100).toFixed(2)
                : "0.00",
          },
          classes: classes.map(c => ({
            id: c.id,
            className: c.className,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher mobile - Get reports error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }
}

export const teacherMobileController = new TeacherMobileController();
